import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Calculator, Info, ChevronDown, ChevronUp, AlertTriangle,
  CheckCircle2, XCircle, Zap, Target, Shield,
} from 'lucide-react';
import { calculateSampleSize, calculatePower, calculatePValue, confidenceLevels } from '../data/abTests';

// ─── Confidence Badge ───
export function ConfidenceBadge({ value }) {
  const color = value >= 95
    ? 'text-emerald-400 border-emerald-500/30 bg-emerald-500/10'
    : value >= 80
    ? 'text-amber-400 border-amber-500/30 bg-amber-500/10'
    : 'text-zinc-400 border-zinc-700 bg-zinc-800/50';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 border font-mono text-[10px] uppercase tracking-wider ${color}`}>
      <Target className='w-3 h-3' />
      {value}%
    </span>
  );
}

// ─── P-Value Display ───
export function PValueDisplay({ pValue }) {
  const { t } = useTranslation();
  const isSignificant = pValue < 0.05;

  return (
    <div className='flex items-center gap-2'>
      <span className={`font-display text-lg ${isSignificant ? 'text-emerald-400' : 'text-zinc-400'}`}>
        {pValue.toFixed(3)}
      </span>
      {isSignificant ? (
        <span className='flex items-center gap-1 text-emerald-400 font-mono text-[10px] uppercase'>
          <CheckCircle2 className='w-3 h-3' />
          {t('ab_testing.significant')}
        </span>
      ) : (
        <span className='flex items-center gap-1 text-zinc-500 font-mono text-[10px] uppercase'>
          <XCircle className='w-3 h-3' />
          {t('ab_testing.not_significant')}
        </span>
      )}
    </div>
  );
}

// ─── Sample Size Calculator ───
export function SampleSizeCalculator({ baselineRate = 10 }) {
  const { t } = useTranslation();
  const [rate, setRate] = useState(baselineRate);
  const [mde, setMde] = useState(10);
  const [confidence, setConfidence] = useState(95);
  const [power, setPower] = useState(80);
  const [expanded, setExpanded] = useState(false);

  const sampleSize = calculateSampleSize(rate / 100, mde, confidence, power);

  return (
    <div className='border border-zinc-800 bg-zinc-950/30'>
      <button
        onClick={() => setExpanded(!expanded)}
        className='w-full flex items-center justify-between px-4 py-3 hover:bg-zinc-800/30 transition-colors'
      >
        <div className='flex items-center gap-2'>
          <Calculator className='w-4 h-4 text-amber-500' />
          <span className='font-display text-sm uppercase'>{t('ab_testing.sample_size_calculator')}</span>
        </div>
        {expanded ? <ChevronUp className='w-4 h-4 text-zinc-500' /> : <ChevronDown className='w-4 h-4 text-zinc-500' />}
      </button>

      {expanded && (
        <div className='px-4 pb-4 space-y-4 border-t border-zinc-800'>
          <div className='grid grid-cols-2 md:grid-cols-4 gap-4 pt-4'>
            <div>
              <label className='font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 block'>
                {t('ab_testing.baseline_rate')}
              </label>
              <div className='flex items-center gap-2'>
                <input
                  type='number'
                  min={0.1}
                  max={100}
                  step={0.1}
                  value={rate}
                  onChange={e => setRate(Math.max(0.1, Number(e.target.value)))}
                  className='w-full bg-zinc-950 border border-zinc-800 focus:border-amber-500 text-white px-2 py-1.5 font-mono text-xs outline-none'
                />
                <span className='font-mono text-xs text-zinc-500'>%</span>
              </div>
            </div>
            <div>
              <label className='font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 block'>
                {t('ab_testing.mde')}
              </label>
              <div className='flex items-center gap-2'>
                <input
                  type='number'
                  min={1}
                  max={100}
                  value={mde}
                  onChange={e => setMde(Math.max(1, Number(e.target.value)))}
                  className='w-full bg-zinc-950 border border-zinc-800 focus:border-amber-500 text-white px-2 py-1.5 font-mono text-xs outline-none'
                />
                <span className='font-mono text-xs text-zinc-500'>%</span>
              </div>
            </div>
            <div>
              <label className='font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 block'>
                {t('ab_testing.confidence')}
              </label>
              <select
                value={confidence}
                onChange={e => setConfidence(Number(e.target.value))}
                className='w-full bg-zinc-950 border border-zinc-800 text-white px-2 py-1.5 font-mono text-xs outline-none'
              >
                {confidenceLevels.map(cl => (
                  <option key={cl.value} value={cl.value}>{cl.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className='font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 block'>
                {t('ab_testing.power')}
              </label>
              <select
                value={power}
                onChange={e => setPower(Number(e.target.value))}
                className='w-full bg-zinc-950 border border-zinc-800 text-white px-2 py-1.5 font-mono text-xs outline-none'
              >
                <option value={80}>80%</option>
                <option value={85}>85%</option>
                <option value={90}>90%</option>
                <option value={95}>95%</option>
              </select>
            </div>
          </div>

          <div className='bg-zinc-900 border border-zinc-700 p-4'>
            <div className='flex items-center gap-3'>
              <Zap className='w-5 h-5 text-amber-500' />
              <div>
                <div className='font-mono text-[10px] uppercase tracking-wider text-zinc-500'>
                  {t('ab_testing.required_sample_per_variant')}
                </div>
                <div className='font-display text-2xl text-amber-400'>
                  {sampleSize > 0 ? sampleSize.toLocaleString() : '—'}
                </div>
              </div>
              <div className='ml-auto text-right'>
                <div className='font-mono text-[10px] uppercase tracking-wider text-zinc-500'>
                  {t('ab_testing.total_required')}
                </div>
                <div className='font-display text-lg text-zinc-300'>
                  {sampleSize > 0 ? (sampleSize * 2).toLocaleString() : '—'}
                </div>
              </div>
            </div>
            <div className='mt-3 flex items-start gap-2 text-zinc-500'>
              <Info className='w-3.5 h-3.5 mt-0.5 shrink-0' />
              <p className='font-mono text-[10px] leading-relaxed'>
                {t('ab_testing.sample_size_note')}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Power Analysis Display ───
export function PowerAnalysis({ test }) {
  const { t } = useTranslation();
  const { stats, impressions, conversions } = test;
  const achievedPower = calculatePower(conversions.a, impressions.a, conversions.b, impressions.b, test.confidenceLevel || 95);

  const powerColor = achievedPower >= 80
    ? 'emerald'
    : achievedPower >= 50
    ? 'amber'
    : 'red';

  return (
    <div className='border border-zinc-800 bg-zinc-950/30 p-4'>
      <h3 className='font-display text-sm uppercase mb-3 flex items-center gap-2'>
        <Shield className='w-4 h-4 text-amber-500' />
        {t('ab_testing.power_analysis')}
      </h3>
      <div className='space-y-3'>
        <div>
          <div className='flex items-center justify-between mb-1'>
            <span className='font-mono text-[10px] uppercase tracking-wider text-zinc-500'>
              {t('ab_testing.achieved_power')}
            </span>
            <span className={`font-display text-lg ${
              powerColor === 'emerald' ? 'text-emerald-400' :
              powerColor === 'amber' ? 'text-amber-400' : 'text-red-400'
            }`}>
              {achievedPower}%
            </span>
          </div>
          <div className='h-2 bg-zinc-900 w-full'>
            <div
              className={`h-full transition-all ${
                powerColor === 'emerald' ? 'bg-emerald-500' :
                powerColor === 'amber' ? 'bg-amber-500' : 'bg-red-500'
              }`}
              style={{ width: `${achievedPower}%` }}
            />
          </div>
        </div>
        <div className='flex items-start gap-2'>
          {achievedPower >= 80 ? (
            <CheckCircle2 className='w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0' />
          ) : (
            <AlertTriangle className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${
              powerColor === 'amber' ? 'text-amber-400' : 'text-red-400'
            }`} />
          )}
          <p className='font-mono text-[10px] text-zinc-400 leading-relaxed'>
            {achievedPower >= 80
              ? t('ab_testing.power_sufficient')
              : t('ab_testing.power_insufficient', { power: achievedPower })
            }
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Full Statistical Summary Panel ───
export function StatisticalSummary({ test }) {
  const { t } = useTranslation();
  const { stats } = test;
  const pValue = stats.pValue || calculatePValue(
    test.conversions.a, test.impressions.a,
    test.conversions.b, test.impressions.b
  );

  return (
    <div className='space-y-4'>
      {/* Statistical Indicators */}
      <div className='border border-zinc-800 bg-zinc-950/30 p-5'>
        <h3 className='font-display text-sm uppercase mb-4 flex items-center gap-2'>
          <Calculator className='w-4 h-4 text-amber-500' />
          {t('ab_testing.statistical_analysis')}
        </h3>

        <div className='grid grid-cols-2 md:grid-cols-3 gap-4'>
          {/* Confidence */}
          <div>
            <div className='font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5'>
              {t('ab_testing.confidence_level')}
            </div>
            <ConfidenceBadge value={stats.confidence} />
            <div className='mt-1'>
              <div className='h-1.5 bg-zinc-900 w-full'>
                <div
                  className={`h-full transition-all ${
                    stats.confidence >= 95 ? 'bg-emerald-500' :
                    stats.confidence >= 80 ? 'bg-amber-500' : 'bg-zinc-600'
                  }`}
                  style={{ width: `${stats.confidence}%` }}
                />
              </div>
            </div>
          </div>

          {/* P-Value */}
          <div>
            <div className='font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5'>
              {t('ab_testing.p_value')}
            </div>
            <PValueDisplay pValue={pValue} />
          </div>

          {/* Lift */}
          <div>
            <div className='font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5'>
              {t('ab_testing.lift')}
            </div>
            <div className={`font-display text-lg ${
              stats.lift > 0 ? 'text-emerald-400' : stats.lift < 0 ? 'text-red-400' : 'text-zinc-400'
            }`}>
              {stats.lift > 0 ? '+' : ''}{stats.lift}%
            </div>
            <div className='font-mono text-[9px] text-zinc-600 mt-0.5'>
              {stats.lift > 0 ? t('ab_testing.better_than_control') : stats.lift < 0 ? t('ab_testing.worse_than_control') : t('ab_testing.no_difference')}
            </div>
          </div>

          {/* Power */}
          <div>
            <div className='font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5'>
              {t('ab_testing.power')}
            </div>
            <div className={`font-display text-lg ${
              stats.power >= 80 ? 'text-emerald-400' : stats.power >= 50 ? 'text-amber-400' : 'text-red-400'
            }`}>
              {stats.power}%
            </div>
          </div>

          {/* Control Rate */}
          <div>
            <div className='font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5'>
              {t('ab_testing.control_rate')}
            </div>
            <span className='font-display text-lg text-amber-400'>{stats.rateA}%</span>
          </div>

          {/* Variation Rate */}
          <div>
            <div className='font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5'>
              {t('ab_testing.variation_rate')}
            </div>
            <span className='font-display text-lg text-sky-400'>{stats.rateB}%</span>
          </div>
        </div>

        {/* Significance Summary */}
        <div className={`mt-4 p-3 border ${
          stats.confidence >= 95 && pValue < 0.05
            ? 'border-emerald-500/30 bg-emerald-500/5'
            : 'border-zinc-700 bg-zinc-900/50'
        }`}>
          <div className='flex items-start gap-2'>
            {stats.confidence >= 95 && pValue < 0.05 ? (
              <CheckCircle2 className='w-4 h-4 text-emerald-400 mt-0.5 shrink-0' />
            ) : (
              <AlertTriangle className='w-4 h-4 text-amber-400 mt-0.5 shrink-0' />
            )}
            <div>
              <div className='font-mono text-[10px] uppercase tracking-wider text-zinc-300'>
                {t('ab_testing.significance_summary')}
              </div>
              <p className='font-mono text-[10px] text-zinc-500 mt-1 leading-relaxed'>
                {stats.confidence >= 95 && pValue < 0.05
                  ? t('ab_testing.significance_yes', { confidence: stats.confidence, pValue })
                  : t('ab_testing.significance_no', { confidence: stats.confidence, pValue })
                }
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Sample Size Calculator */}
      <SampleSizeCalculator baselineRate={stats.rateA} />

      {/* Power Analysis */}
      {test.status !== 'draft' && <PowerAnalysis test={test} />}
    </div>
  );
}
